import { ApiResponse, PaginatedApiResponse } from "@/types/api.types";
import baseApi from "./RootBaseApi";
import {
  PromoListQueryParams,
  UpdatePromoStatusPayload,
  IPromoData,
} from "@/types/promo-management.types";

type RawPromoListItem = {
  id: string;
  name: string;
  code: string;
  start_date: string;
  end_date: string;
  coupon_type?: number | null;
  discount_type: number;
  discount_amount?: number | null;
  discount_percentage?: number | null;
  max_discount?: string | number | null;
  min_order_value?: string | number | null;
  total_quantity?: number | null;
  used_count?: number | null;
  remaining_usage_count?: number | null;
  customer_usage_limit?: number | null;
  description?: string | null;
  is_restricted_to_groups?: boolean | null;
  is_supplier_promotion?: boolean | null;
  is_hidden?: boolean | null;
  eligibility_rule?: number | null;
  linked_club_id?: string | null;
  status: number;
  created_at?: string | null;
  updated_at?: string | null;
};

export const PromoManagementService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPromos: builder.query<
      PaginatedApiResponse<IPromoData[]>,
      PromoListQueryParams
    >({
      query: (params) => ({
        url: "coupons",
        method: "GET",
        params: {
          ...params,
        },
      }),
      transformResponse: (
        response: PaginatedApiResponse<RawPromoListItem[]>,
      ) => {
        const mappedData: IPromoData[] = (
          response.data as RawPromoListItem[]
        ).map((item: RawPromoListItem) => ({
          id: item.id,
          name: item.name ?? "",
          code: item.code ?? "",
          start_date: item.start_date ?? "",
          end_date: item.end_date ?? "",
          coupon_type: item.coupon_type ?? undefined,
          discount_amount: item.discount_amount ?? undefined,
          discount_percentage: item.discount_percentage ?? undefined,
          max_discount:
            item.max_discount !== undefined && item.max_discount !== null
              ? Number(item.max_discount)
              : undefined,
          discount_type: item.discount_type,
          min_order_value:
            item.min_order_value !== undefined && item.min_order_value !== null
              ? Number(item.min_order_value)
              : 0,
          total_quantity: item.total_quantity ?? undefined,
          remaining_usage_count:
            item.remaining_usage_count ??
            (item.total_quantity != null
              ? Number(item.total_quantity) - Number(item.used_count ?? 0)
              : undefined),
          customer_usage_limit: item.customer_usage_limit ?? undefined,
          description: item.description ?? "",
          is_restricted_to_groups: item.is_restricted_to_groups ?? false,
          is_supplier_promotion: item.is_supplier_promotion ?? false,
          is_hidden: item.is_hidden ?? false,
          eligibility_rule: item.eligibility_rule ?? undefined,
          linked_club_id: item.linked_club_id ?? undefined,
          status: item.status,
          created_at: item.created_at ?? undefined,
          updated_at: item.updated_at ?? undefined,
        }));

        return {
          ...response,
          data: mappedData,
          // unify pagination keys from snake_case to camelCase while preserving originals
          totalItems: response.totalItems ?? response.total_items,
          totalPages: response.totalPages ?? response.total_pages,
          currentPage: response.currentPage ?? response.current_page,
        } as PaginatedApiResponse<IPromoData[]>;
      },
      providesTags: ["Promos"],
      keepUnusedDataFor: 0,
    }),

    getPromoById: builder.query<IPromoData, string>({
      query: (promo_id) => ({
        url: `coupons/${promo_id}`,
        method: "GET",
      }),
      transformResponse: (response: unknown): IPromoData => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const item = (response as any)?.data ?? (response as any);
        const mapped: IPromoData = {
          id: item.id,
          name: item.name ?? "",
          code: item.code ?? "",
          start_date: item.start_date ?? "",
          end_date: item.end_date ?? "",
          coupon_type: item.coupon_type ?? undefined,
          discount_amount: item.discount_amount ?? undefined,
          discount_percentage: item.discount_percentage ?? undefined,
          max_discount:
            item.max_discount !== undefined && item.max_discount !== null
              ? Number(item.max_discount)
              : undefined,
          discount_type: item.discount_type,
          min_order_value:
            item.min_order_value !== undefined && item.min_order_value !== null
              ? Number(item.min_order_value)
              : 0,
          total_quantity: item.total_quantity ?? undefined,
          remaining_usage_count:
            item.remaining_usage_count ??
            (item.total_quantity != null
              ? Number(item.total_quantity) - Number(item.used_count ?? 0)
              : undefined),
          customer_usage_limit: item.customer_usage_limit ?? undefined,
          description: item.description ?? "",
          is_restricted_to_groups: item.is_restricted_to_groups ?? false,
          is_supplier_promotion: item.is_supplier_promotion ?? false,
          is_hidden: item.is_hidden ?? false,
          eligibility_rule: item.eligibility_rule ?? undefined,
          linked_club_id: item.linked_club_id ?? undefined,
          status: item.status,
          created_at: item.created_at ?? undefined,
          updated_at: item.updated_at ?? undefined,
        };
        return mapped;
      },
      providesTags: (result, error, promo_id) => [
        { type: "Promos", id: promo_id },
      ],
      keepUnusedDataFor: 0,
    }),

    createPromo: builder.mutation<ApiResponse<void>, Partial<IPromoData>>({
      query: (data) => ({
        url: "coupons",
        method: "POST",
        data,
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["Promos"],
    }),

    updatePromo: builder.mutation<
      ApiResponse<void>,
      { promo_id: string; data: Partial<IPromoData> }
    >({
      query: ({
        promo_id,
        data,
      }: {
        promo_id: string;
        data: Partial<IPromoData>;
      }) => ({
        url: `coupons/${promo_id}`,
        method: "PUT",
        data,
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["Promos"],
    }),

    deletePromo: builder.mutation<ApiResponse<void>, string>({
      query: (promo_id) => ({
        url: `coupons/${promo_id}`,
        method: "DELETE",
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["Promos"],
    }),

    updatePromoStatus: builder.mutation<
      ApiResponse<void>,
      UpdatePromoStatusPayload
    >({
      query: ({ promoId, status }) => ({
        url: "coupons/status",
        method: "PATCH",
        data: { promoId, status },
      }),
      transformResponse: (response: ApiResponse<void>) => response,
      invalidatesTags: ["Promos"],
    }),
  }),
});

export const {
  useGetPromosQuery,
  useGetPromoByIdQuery,
  useCreatePromoMutation,
  useUpdatePromoMutation,
  useDeletePromoMutation,
  useUpdatePromoStatusMutation,
} = PromoManagementService;
